import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Delete, LogIn } from 'lucide-react';

export default function LoginPage() {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const pinRef = useRef('');
  const submittingRef = useRef(false);

  // Mantém a ref sincronizada para uso no listener de teclado (que não
  // reage a mudanças de estado por causa do closure).
  useEffect(() => { pinRef.current = pin; }, [pin]);

  const handleNumpad = useCallback((num) => {
    setError('');
    setPin((prev) => (prev.length < 6 ? prev + num : prev));
  }, []);

  const handleDelete = useCallback(() => {
    setError('');
    setPin((prev) => prev.slice(0, -1));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return; // evita double-click
    const current = pinRef.current;
    if (current.length < 4) {
      setError('PIN deve ter no mínimo 4 dígitos');
      return;
    }
    submittingRef.current = true;
    const result = await login(current);
    submittingRef.current = false;
    if (result.success) {
      navigate('/');
    } else {
      setError('PIN inválido');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPin('');
    }
  }, [login, navigate]);

  // Fase 23 — Suporte ao teclado físico (mais rápido que tocar na tela).
  // Padrão "stable callback via ref": o handler real fica numa ref que é
  // atualizada em todo render, mas o listener no `window` é registrado UMA
  // ÚNICA VEZ no mount e removido APENAS no unmount. Sem isso, qualquer
  // mudança nos callbacks deps (login/navigate) remontaria o listener no
  // meio da digitação, podendo dropar uma tecla rápida do scanner ou PIN.
  const onKeyRef = useRef(null);
  useEffect(() => {
    onKeyRef.current = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleNumpad(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleDelete();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setPin('');
        setError('');
      }
    };
  }, [handleNumpad, handleDelete, handleSubmit]);

  useEffect(() => {
    const stableListener = (e) => onKeyRef.current && onKeyRef.current(e);
    window.addEventListener('keydown', stableListener);
    return () => window.removeEventListener('keydown', stableListener);
  }, []);

  const numpadKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'enter'],
  ];

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-8"
    >
      <div className="w-full max-w-sm animate-in fade-in duration-700">
        {/* Brand - Logo transparente sem sombras nem bordas (Fase 15) */}
        <div className="text-center mb-10 animate-in fade-in duration-[800ms]">
          <img
            data-testid="login-brand-logo"
            src="/logo-transparente.webp"
            alt="Kaven Restaurante"
            className="block mx-auto object-contain w-full"
            style={{ maxWidth: '280px' }}
          />
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-400 mt-4">
            Sistema Local
          </p>
        </div>

        {/* PIN Display */}
        <div
          className={`bg-white border-2 border-zinc-200 rounded-xl p-6 mb-6 text-center ${
            shake ? 'animate-shake' : ''
          }`}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 mb-3">
            Digite seu PIN
          </p>
          <div data-testid="pin-display" className="flex justify-center gap-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full transition-all duration-150 ${
                  i < pin.length ? 'bg-[#2563eb] scale-110' : 'bg-zinc-200'
                }`}
              />
            ))}
          </div>
          {error && (
            <p data-testid="login-error" className="text-red-500 text-sm font-semibold mt-3">
              {error}
            </p>
          )}
        </div>

        {/* Numpad */}
        <div data-testid="login-numpad" className="grid grid-cols-3 gap-3">
          {numpadKeys.flat().map((key) => {
            if (key === 'clear') {
              return (
                <button
                  key={key}
                  data-testid="login-numpad-clear"
                  onClick={handleDelete}
                  className="bg-white text-zinc-600 text-xl font-bold rounded-xl border-2 border-zinc-200 shadow-sm hover:border-zinc-400 active:bg-zinc-100 active:translate-y-[2px] transition-all duration-150 py-5 flex items-center justify-center"
                >
                  <Delete size={24} />
                </button>
              );
            }
            if (key === 'enter') {
              return (
                <button
                  key={key}
                  data-testid="login-numpad-enter"
                  onClick={handleSubmit}
                  className="bg-[#2563eb] text-white text-xl font-bold rounded-xl border-2 border-transparent hover:bg-[#1d4ed8] active:translate-y-[2px] transition-all duration-150 py-5 flex items-center justify-center"
                >
                  <LogIn size={24} />
                </button>
              );
            }
            return (
              <button
                key={key}
                data-testid={`login-numpad-${key}`}
                onClick={() => handleNumpad(key)}
                className="bg-white text-zinc-900 text-2xl font-bold rounded-xl border-2 border-zinc-200 shadow-sm hover:border-zinc-900 active:bg-zinc-100 active:translate-y-[2px] transition-all duration-150 py-5"
              >
                {key}
              </button>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-400 mt-6 uppercase tracking-wider">
          Teclado físico OK
        </p>
      </div>
    </div>
  );
}
